<template>
  <div
    class="application-component protection-strategy-component"
    v-loading="loading"
    :element-loading-text="loadingText"
  >
    <div class="application-component-title">保护策略</div>
    <div
      class="application-component-content"
      :style="{ fontSize }"
    >
      <div v-if="readOnly" class="placeholder">
        <el-skeleton :rows="6" animated />
      </div>
      <template v-else>
        <div v-if="!hasDependencies" class="dependency-tip">
          <div class="tip-title">保护策略依赖特征拆解和新颖性分析结果</div>
          <div class="tip-desc">请先完成“特征拆解”和“新颖性分析”，系统再生成保护范围表。</div>
        </div>

        <template v-else>
          <div v-if="sourceStale && !loading" class="stale-tip">
            <span>上游分析结果已更新，保护范围表正在等待重新生成。</span>
            <!-- <el-button size="small" text @click="handleGenerate()">重新生成</el-button> -->
          </div>

          <div v-if="rows.length" class="strategy-table-scroll">
            <table class="strategy-table">
              <thead>
                <tr>
                  <th class="code-column">编号</th>
                  <th class="feature-column">技术特征</th>
                  <th class="status-column">新颖性状态</th>
                  <th class="claim-column">权要类型</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="(row, index) in rows"
                  :key="row.featureId || row.code || index"
                  class="strategy-row"
                  @click="handleFocusRow(row)"
                >
                  <td class="code-column">
                    <div class="row-code">{{ row.code || `F${index + 1}` }}</div>
                  </td>
                  <td class="feature-column">
                    <div class="cell-card">
                      <el-input
                        v-model="row.content"
                        type="textarea"
                        :autosize="{ minRows: 2, maxRows: 5 }"
                        resize="none"
                        @click.stop
                        @blur="handleRowBlur"
                      />
                      <div class="source-text" :class="{ empty: !row.sourceRanges?.length }">
                        {{ buildSourceSummary(row) }}
                      </div>
                    </div>
                  </td>
                  <td class="status-column">
                    <div class="cell-card compact" @click.stop>
                      <el-select
                        v-model="row.noveltyStatus"
                        class="cell-select"
                        @change="handleNoveltyStatusChange(row)"
                      >
                        <el-option
                          v-for="option in noveltyStatusOptions"
                          :key="option.value"
                          :label="option.label"
                          :value="option.value"
                        />
                      </el-select>
                      <div class="status-preview" :class="getStatusClass(row.noveltyStatus)">
                        {{ row.noveltyStatusLabel }}
                      </div>
                    </div>
                  </td>
                  <td class="claim-column">
                    <div class="cell-card" @click.stop>
                      <el-select
                        v-model="row.claimType"
                        class="cell-select"
                        @change="handleClaimTypeChange(row)"
                      >
                        <el-option
                          v-for="option in claimTypeOptions"
                          :key="option.value"
                          :label="option.label"
                          :value="option.value"
                        />
                      </el-select>
                      <div class="claim-preview">{{ row.claimTypeLabel }}</div>
                      <div v-if="row.reason" class="claim-reason">{{ row.reason }}</div>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <el-empty v-else description="正在等待保护范围表生成" />

          <div class="knowledge-panel">
            <div class="knowledge-title">权要类型说明</div>
            <div class="knowledge-grid">
              <div v-for="item in knowledgeCards" :key="item.title" class="knowledge-card">
                <div class="knowledge-card-title">{{ item.title }}</div>
                <div class="knowledge-card-desc">{{ item.desc }}</div>
                <div class="knowledge-card-meta">{{ item.meta }}</div>
              </div>
            </div>
          </div>
        </template>
      </template>
    </div>
  </div>
</template>

<script setup>
import { ElMessage } from 'element-plus'
import { computed, onUnmounted, ref, watch } from 'vue'
import { useGlobalStore } from '@/store/modules/global'
import { useWritingStore } from '@/store/modules/writing'
import {
  fetchGenerateProtectionStrategy,
  fetchSaveComponent
} from '@/api/writing'
import { jsonParse } from '@/utils'

const COMPONENT_TYPE_CODE = 'comp_prs'
const FEATURE_BREAKDOWN_COMPONENT_CODE = 'comp_fea'
const NOVELTY_COMPONENT_CODE = 'comp_nov'
const FEATURE_BREAKDOWN_EVENT = 'feature-breakdown-focus'

const CLAIM_TYPE_OPTIONS = [
  { value: 'INDEPENDENT_CORE', label: '独立权利要求核心特征' },
  { value: 'INDEPENDENT_COMPONENT', label: '独立权利要求步骤/组成' },
  { value: 'DEPENDENT_LIMITATION', label: '从属权利要求限定' },
  { value: 'EMBODIMENT', label: '实施例特征' },
]

const NOVELTY_STATUS_OPTIONS = [
  { value: 'UNDISCLOSED', label: '未公开' },
  { value: 'PARTIAL', label: '部分公开' },
  { value: 'DISCLOSED', label: '已公开' },
]

const KNOWLEDGE_CARDS = [
  {
    title: '独立权利要求核心特征',
    desc: '构成发明核心技术方案，通常对应最关键的区别点。',
    meta: '写作位置：独立权利要求',
  },
  {
    title: '独立权利要求步骤/组成',
    desc: '属于独权必要技术步骤或结构，支撑核心方案完整成立。',
    meta: '写作位置：独立权利要求',
  },
  {
    title: '从属权利要求限定',
    desc: '对核心方案进行约束、优化或补充限定。',
    meta: '写作位置：从属权利要求',
  },
  {
    title: '实施例特征',
    desc: '主要用于说明具体实施方式，通常不作为主要保护点。',
    meta: '写作位置：说明书实施例',
  },
]

const globalStore = useGlobalStore()
const writingStore = useWritingStore()

const props = defineProps({
  readOnly: {
    type: Boolean,
    default: false
  },
  data: {
    type: Object,
    default: () => null
  },
  projectId: {
    type: String,
    default: ''
  },
  handleFocusMainFileSource: {
    type: Function,
    default: null
  }
})

const resultData = ref(null)
const generating = ref(false)
const saving = ref(false)
const localExtOne = ref('')
const lastAutoSourceVersion = ref('')

let autoGenerateTimer = null

const fontSize = computed(() => `${globalStore.fontSize}px`)
const loading = computed(() => generating.value || saving.value)
const loadingText = computed(() => {
  if (generating.value) {
    return '正在思考中...'
  }
  if (saving.value) {
    return '正在思考中...'
  }
  return ''
})

const featureRaw = computed(() => writingStore.step2Info?.step2Component?.[FEATURE_BREAKDOWN_COMPONENT_CODE]?.docResult || '')
const noveltyRaw = computed(() => writingStore.step2Info?.step2Component?.[NOVELTY_COMPONENT_CODE]?.docResult || '')

const featureItems = computed(() => {
  const parsed = jsonParse(featureRaw.value, [])
  return Array.isArray(parsed) ? parsed : []
})

const noveltyRows = computed(() => {
  const parsed = jsonParse(noveltyRaw.value, {})
  return Array.isArray(parsed?.rows) ? parsed.rows : []
})

const hasDependencies = computed(() => featureItems.value.length > 0 && noveltyRows.value.length > 0)
const currentSourceVersion = computed(() => buildSourceVersion(featureRaw.value, noveltyRaw.value))
const rows = computed(() => resultData.value?.rows || [])
const sourceStale = computed(() => {
  if (!resultData.value?.sourceVersion || !hasDependencies.value) {
    return false
  }
  return resultData.value.sourceVersion !== currentSourceVersion.value
})

const noveltyStatusOptions = NOVELTY_STATUS_OPTIONS
const claimTypeOptions = CLAIM_TYPE_OPTIONS
const knowledgeCards = KNOWLEDGE_CARDS

const hashJavaString = (value = '') => {
  let hash = 0
  for (let index = 0; index < value.length; index += 1) {
    hash = ((hash * 31) + value.charCodeAt(index)) | 0
  }
  return (hash >>> 0).toString(16)
}

const buildSourceVersion = (featureValue, noveltyValue) => {
  return hashJavaString(`${featureValue || ''}||${noveltyValue || ''}`)
}

const formatNow = () => {
  const now = new Date()
  const pad = (value) => String(value).padStart(2, '0')
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())} ${pad(now.getHours())}:${pad(now.getMinutes())}:${pad(now.getSeconds())}`
}

const getNoveltyStatusLabel = (status) => {
  const matched = noveltyStatusOptions.find(item => item.value === status)
  return matched?.label || '未公开'
}

const getClaimTypeLabel = (claimType) => {
  const matched = claimTypeOptions.find(item => item.value === claimType)
  return matched?.label || claimTypeOptions[0].label
}

const normalizeSourceRanges = (sourceRanges = []) => {
  if (!Array.isArray(sourceRanges)) {
    return []
  }

  return sourceRanges
    .map((item) => {
      const paragraphIndex = Number(item?.paragraphIndex)
      const startOffset = Number(item?.startOffset)
      const endOffset = Number(item?.endOffset)

      return {
        paragraphIndex: Number.isFinite(paragraphIndex) ? paragraphIndex : null,
        anchorId: item?.anchorId || (Number.isFinite(paragraphIndex) ? `note-paragraph-${paragraphIndex}` : ''),
        startOffset: Number.isFinite(startOffset) ? startOffset : null,
        endOffset: Number.isFinite(endOffset) ? endOffset : null,
        quote: item?.quote || '',
      }
    })
    .filter(item =>
      Number.isInteger(item.paragraphIndex) &&
      Number.isInteger(item.startOffset) &&
      Number.isInteger(item.endOffset) &&
      item.endOffset > item.startOffset
    )
}

const normalizeParagraphIndices = (sourceRanges, paragraphIndices = []) => {
  return Array.from(new Set([
    ...(Array.isArray(paragraphIndices) ? paragraphIndices : []),
    ...sourceRanges.map(item => item.paragraphIndex),
  ].map(value => Number(value)).filter(value => Number.isInteger(value))))
}

const normalizeRow = (item = {}, index = 0) => {
  const sourceRanges = normalizeSourceRanges(item?.sourceRanges)
  const noveltyStatus = noveltyStatusOptions.some(option => option.value === item?.noveltyStatus)
    ? item.noveltyStatus
    : 'UNDISCLOSED'
  const claimType = claimTypeOptions.some(option => option.value === item?.claimType)
    ? item.claimType
    : 'INDEPENDENT_CORE'

  return {
    featureId: item?.featureId || `protection-feature-${index}`,
    code: item?.code || `F${index + 1}`,
    content: item?.content || '',
    noveltyStatus,
    noveltyStatusLabel: getNoveltyStatusLabel(noveltyStatus),
    claimType,
    claimTypeLabel: getClaimTypeLabel(claimType),
    reason: item?.reason || '',
    sourceRanges,
    paragraphIndices: normalizeParagraphIndices(sourceRanges, item?.paragraphIndices),
  }
}

const normalizeResult = (value) => {
  const parsed = typeof value === 'string' ? jsonParse(value, null) : value
  if (!parsed || typeof parsed !== 'object') {
    return null
  }

  const rowList = Array.isArray(parsed.rows) ? parsed.rows.map(normalizeRow) : []
  return {
    sourceVersion: parsed.sourceVersion || '',
    updatedAt: parsed.updatedAt || '',
    featureCount: Number(parsed.featureCount || rowList.length),
    rowCount: Number(parsed.rowCount ?? rowList.length),
    rows: rowList,
  }
}

const createBaseResult = () => {
  return {
    sourceVersion: currentSourceVersion.value,
    updatedAt: formatNow(),
    featureCount: rows.value.length,
    rowCount: rows.value.length,
    rows: [],
  }
}

const syncStepState = (nextResult, extOne = localExtOne.value) => {
  const normalized = normalizeResult(nextResult)
  if (!normalized) {
    return
  }

  resultData.value = normalized
  writingStore.setStep2Info({
    ...(writingStore.step2Info || {}),
    step2Component: {
      ...(writingStore.step2Info?.step2Component || {}),
      [COMPONENT_TYPE_CODE]: {
        ...(writingStore.step2Info?.step2Component?.[COMPONENT_TYPE_CODE] || {}),
        ...(props.data || {}),
        docResult: JSON.stringify(normalized),
        extOne,
      }
    }
  })
}

const persistResult = async (nextResult) => {
  const normalized = normalizeResult(nextResult)
  if (!normalized || !props.projectId) {
    return
  }

  normalized.sourceVersion = normalized.sourceVersion || currentSourceVersion.value
  normalized.updatedAt = formatNow()
  normalized.featureCount = normalized.rows.length
  normalized.rowCount = normalized.rows.length

  saving.value = true
  try {
    await fetchSaveComponent({
      disclosureProjectId: props.projectId,
      componentTypeCode: COMPONENT_TYPE_CODE,
      docResult: JSON.stringify(normalized),
      extOne: localExtOne.value || props.data?.extOne || '',
    })
    syncStepState(normalized, localExtOne.value || props.data?.extOne || '')
  } finally {
    saving.value = false
  }
}

const applyBackendResult = (result, successText = '') => {
  const normalized = normalizeResult(result)
  if (!normalized) {
    throw new Error('invalid_protection_strategy_result')
  }

  syncStepState(normalized, localExtOne.value || props.data?.extOne || '')
  if (successText) {
    ElMessage.success(successText)
  }
}

const handleGenerate = async ({ auto = false } = {}) => {
  if (!props.projectId || !hasDependencies.value || generating.value) {
    return
  }

  generating.value = true
  try {
    const result = await fetchGenerateProtectionStrategy(props.projectId)
    applyBackendResult(result, auto ? '' : '保护策略已更新')
  } catch (error) {
    console.error('generate protection strategy failed', error)
    ElMessage.error(auto ? '保护策略自动更新失败' : '保护策略生成失败')
  } finally {
    generating.value = false
  }
}

const scheduleAutoGenerate = () => {
  if (!hasDependencies.value || props.readOnly || generating.value) {
    return
  }

  if (resultData.value?.sourceVersion === currentSourceVersion.value && rows.value.length) {
    return
  }

  if (lastAutoSourceVersion.value === currentSourceVersion.value) {
    return
  }

  if (autoGenerateTimer) {
    clearTimeout(autoGenerateTimer)
  }

  autoGenerateTimer = setTimeout(async () => {
    lastAutoSourceVersion.value = currentSourceVersion.value
    await handleGenerate({ auto: true })
  }, 400)
}

const buildSourceSummary = (row) => {
  const paragraphIndices = normalizeParagraphIndices(row?.sourceRanges || [], row?.paragraphIndices || [])
  if (!paragraphIndices.length) {
    return '未关联原文片段'
  }
  return `关联段落：${paragraphIndices.join('、')}`
}

const handleFocusRow = (row) => {
  if (!Array.isArray(row?.sourceRanges) || !row.sourceRanges.length) {
    ElMessage.warning('当前特征未关联原文片段，请重新生成保护策略')
    return
  }

  if (props.handleFocusMainFileSource) {
    props.handleFocusMainFileSource({
      itemId: row?.featureId || row?.code || '',
      sourceRanges: row.sourceRanges,
    })
    return
  }

  window.dispatchEvent(new CustomEvent(FEATURE_BREAKDOWN_EVENT, {
    detail: {
      projectId: props.projectId,
      sourceRanges: row.sourceRanges,
    }
  }))
}

const handleNoveltyStatusChange = async (row) => {
  row.noveltyStatusLabel = getNoveltyStatusLabel(row.noveltyStatus)
  try {
    await persistResult({
      ...(resultData.value || createBaseResult()),
      rows: rows.value,
    })
  } catch (error) {
    console.error('save protection strategy status failed', error)
    ElMessage.error('保护策略保存失败')
  }
}

const handleClaimTypeChange = async (row) => {
  row.claimTypeLabel = getClaimTypeLabel(row.claimType)
  try {
    await persistResult({
      ...(resultData.value || createBaseResult()),
      rows: rows.value,
    })
  } catch (error) {
    console.error('save protection strategy claim type failed', error)
    ElMessage.error('保护策略保存失败')
  }
}

const handleRowBlur = async () => {
  try {
    await persistResult({
      ...(resultData.value || createBaseResult()),
      rows: rows.value,
    })
  } catch (error) {
    console.error('save protection strategy failed', error)
    ElMessage.error('保护策略保存失败')
  }
}

const getStatusClass = (status) => {
  if (status === 'DISCLOSED') {
    return 'is-disclosed'
  }
  if (status === 'PARTIAL') {
    return 'is-partial'
  }
  return 'is-undisclosed'
}

watch(
  () => props.data?.docResult,
  (newValue) => {
    resultData.value = normalizeResult(newValue)
  },
  { immediate: true }
)

watch(
  () => props.data?.extOne,
  (newValue) => {
    localExtOne.value = newValue || ''
  },
  { immediate: true }
)

watch(
  () => props.projectId,
  () => {
    lastAutoSourceVersion.value = ''
    if (autoGenerateTimer) {
      clearTimeout(autoGenerateTimer)
      autoGenerateTimer = null
    }
  },
  { immediate: true }
)

watch(
  () => `${featureRaw.value}||${noveltyRaw.value}`,
  () => {
    scheduleAutoGenerate()
  },
  { immediate: true }
)

onUnmounted(() => {
  if (autoGenerateTimer) {
    clearTimeout(autoGenerateTimer)
  }
})
</script>

<style scoped lang="scss">
@import './common.scss';

.protection-strategy-component {
  position: relative;
  overflow: hidden;
}

.dependency-tip,
.stale-tip,
.panel-head,
.knowledge-panel {
  padding: 14px 16px;
  border-radius: 14px;
}

.dependency-tip {
  background: linear-gradient(180deg, #f8fafc 0%, #f1f5f9 100%);
  border: 1px solid #dbe4ee;
}

.tip-title,
.panel-title,
.knowledge-title {
  color: #111827;
  font-weight: 600;
}

.tip-desc,
.panel-meta {
  margin-top: 6px;
  color: #4b5563;
  line-height: 1.7;
}

.stale-tip {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 14px;
  color: #166534;
  background: #f0fdf4;
  border: 1px solid #bbf7d0;
}

.panel-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  background: #fff;
  border: 1px solid #e5e7eb;
}

.panel-meta {
  margin-top: 4px;
  font-size: 12px;
}

.panel-actions {
  display: flex;
  align-items: center;
}

.strategy-table-scroll {
  margin-top: 16px;
  overflow: auto;
  border: 1px solid #e5e7eb;
  border-radius: 16px;
  background: #fff;
}

.strategy-table {
  width: 100%;
  min-width: 920px;
  border-collapse: collapse;

  th,
  td {
    padding: 14px 12px;
    vertical-align: top;
    border-bottom: 1px solid #eef2f7;
  }

  th {
    position: sticky;
    top: 0;
    z-index: 1;
    color: #475569;
    font-size: 12px;
    font-weight: 600;
    text-align: left;
    background: #f8fafc;
  }

  tbody tr:last-child td {
    border-bottom: none;
  }
}

.strategy-row {
  transition: background 0.2s ease;

  &:hover {
    background: #fafafa;
  }
}

.code-column {
  width: 88px;
  min-width: 88px;
}

.feature-column {
  min-width: 340px;
}

.status-column {
  width: 180px;
  min-width: 180px;
}

.claim-column {
  width: 280px;
  min-width: 280px;
}

.row-code {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 48px;
  height: 30px;
  padding: 0 10px;
  color: #111827;
  font-weight: 600;
  background: #f3f4f6;
  border-radius: 999px;
}

.cell-card {
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 10px;
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 12px;

  &.compact {
    min-height: 106px;
  }
}

.cell-select {
  width: 100%;
}

.source-text,
.claim-reason {
  font-size: 12px;
  line-height: 1.6;
}

.source-text {
  color: #0f766e;

  &.empty {
    color: #9ca3af;
  }
}

.status-preview,
.claim-preview {
  display: inline-flex;
  align-items: center;
  width: fit-content;
  max-width: 100%;
  padding: 5px 10px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 600;
}

.status-preview {
  color: #111827;
  background: #f3f4f6;

  &.is-undisclosed {
    color: #0f766e;
    background: #ecfdf5;
  }

  &.is-partial {
    color: #1f2937;
    background: #f3f4f6;
  }

  &.is-disclosed {
    color: #374151;
    background: #e5e7eb;
  }
}

.claim-preview {
  color: #14532d;
  background: #f0fdf4;
}

.claim-reason {
  color: #6b7280;
}

.knowledge-panel {
  margin-top: 16px;
  background: linear-gradient(180deg, #ffffff 0%, #f8fbf9 100%);
  border: 1px solid #e5e7eb;
}

.knowledge-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
  margin-top: 12px;
}

.knowledge-card {
  padding: 14px;
  background: #fff;
  border: 1px solid #e5e7eb;
  border-left: 3px solid #16a34a;
  border-radius: 12px;
}

.knowledge-card-title {
  color: #111827;
  font-weight: 600;
}

.knowledge-card-desc,
.knowledge-card-meta {
  margin-top: 6px;
  color: #4b5563;
  line-height: 1.7;
}

.knowledge-card-meta {
  font-size: 12px;
}

.placeholder {
  :deep(.el-skeleton__item) {
    border-radius: 8px;
  }
}

:deep(.el-textarea__inner) {
  min-height: 56px !important;
}

@media (max-width: 1200px) {
  .knowledge-grid {
    grid-template-columns: 1fr;
  }
}
</style>
